import { useMutation } from '@tanstack/react-query';

import { EVENT, PKG } from '../../analytics/analyticsConsts';
import { setProveStatus } from '../../app/applicationScreenSlice';
import { setConsumerProfile } from '../../app/consumerInfoSlice';
import { useImpState } from '../../app/hooks';
import { setEmailError } from '../../app/validatorSlice';
import { ANALYTICS } from '../../firebase/firebase';
import { DeepRequired } from '../../types/utility';
import { toTitleCase } from '../../utils/formatUtils';
import { ValidationResponse } from '../../utils/validateUtils';
import { ImprintError } from '../common/error';
import {
  getPrefillData,
  type GetPrefillDataArgs,
  type GetPrefillDataResponse,
} from './getPrefillData.api';

export default function useGetPrefillData() {
  const { dispatch, consumerProfile } = useImpState();

  return useMutation<GetPrefillDataResponse, ImprintError, GetPrefillDataArgs>({
    mutationFn: getPrefillData,
    retry: false, // this API call takes a long time, we can't maintain this loading state for too long
    onSuccess: (data) => {
      ANALYTICS.logEvent(EVENT.PERSONAL_INFO_PREFILLED);
      if (data.email) {
        dispatch(setProveStatus('success'));
      } else {
        dispatch(setProveStatus('noEmail'));
        dispatch(setEmailError(ValidationResponse.needEmail));
      }

      if (!validData(data)) {
        ANALYTICS.funcError(
          PKG,
          'useGetPrefillData',
          'prove pre-filled data but it is incomplete or invalid'
        );
        dispatch(setProveStatus('fail'));
        return;
      }

      // write data to redux
      dispatch(
        setConsumerProfile({
          ...consumerProfile,
          name: {
            firstName: toTitleCase(data.firstName),
            lastName: toTitleCase(data.lastName),
          },
          address: {
            ...consumerProfile.address,
            streetLine1: data.address.streetLine1,
            streetLine2: data.address.streetLine2 || '',
            city: data.address.city,
            state: data.address.state,
            zipCode: data.address.zipCode,
          },
          dob: {
            year: data.dob.year,
            month: data.dob.month,
            day: data.dob.day,
          },
          ssn9: data.ssn9,
          email: data.email,
        })
      );
    },
    onError: () => {
      dispatch(setProveStatus('fail'));
    },
  });
}

const validData = (
  data: GetPrefillDataResponse
): data is DeepRequired<GetPrefillDataResponse> =>
  !!(
    data.firstName &&
    data.lastName &&
    data.address?.streetLine1 &&
    data.address?.city &&
    data.address?.state &&
    data.address?.zipCode &&
    data.dob?.year &&
    data.dob?.month &&
    data.dob?.day
  );
